// Shared admin controls — ONE button language and ONE section card across
// the admin area (2026-07-25 per Nika "check all buttons … fix the whole
// admin area"). Every admin page hand-rolled its own tag:'button' with the
// same padding/round/border/hover triple, and its own card; these recipes
// replace them (module.js's own ghost / primary buttons, card and tab were
// folded into these on 2026-09-24).

// The quiet secondary action. Rest = the `overlay` wash, hover = the house
// hover wash, press = the selected step, matching shared/AiActionConfirmCard
// .RejectBtn — the same "ONE button language" reasoning this file's own
// header comment already applies to spacing.
//
// DISABLED — the brand's one disabled law (brand-language skill, company
// 282deae): the real `disabled` attribute, `cursor: not-allowed` and the
// `field-disabled` pair (the `disabled` ink on the neutral disabled plate).
// Never opacity: `.5` dragged the label under 4.5:1 on both themes.
export const AdminActionBtn = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center',
  gap: 'V',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusPill',
  border: 'none',
  background: 'overlay',
  color: 'title',
  fontSize: 'Z2',
  fontWeight: '500',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'Z defaultBezier background',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  ':disabled': { theme: 'field-disabled' }
}

// The ONE compact variant (brand geometry: `controlHCompact`, the `Z` size) —
// for an action inside a dense table row or a chip line. A call site never
// shrinks AdminActionBtn's padding itself (Rule 68): the height is a
// min-height, so only the horizontal inset would move and the capsule goes
// lopsided.
export const AdminActionBtnCompact = {
  extends: 'AdminActionBtn',
  minHeight: 'controlHCompact',
  padding: '0 Z1',
  fontSize: 'Z'
}

// The icon-only compact action: a `controlHCompact` square, quiet at rest
// (no plate — it sits beside the text it acts on), the hover / press steps and
// the disabled law from the primitive. The call site passes the Icon and an
// accessible name.
export const AdminIconBtn = {
  extends: 'AdminActionBtnCompact',
  justifyContent: 'center',
  minWidth: 'controlHCompact',
  padding: '0',
  background: 'transparent',
  color: 'caption',
  ':hover': { background: 'hover', color: 'title' }
}

// A destructive action — the quiet compact control in the AA-safe danger ink
// (the /admin/danger recipe: ink on the quiet plate, never a hue slab and
// never the raw brand `red`, which read 2.45:1 as a label).
export const AdminActionBtnDanger = {
  extends: 'AdminActionBtnCompact',
  color: 'dangerInk'
}

// The page's ONE primary action — solid ink: the brand `primary` theme
// (white on the dark ground, near-black on the light), which carries its own
// hover / focus / press steps (Rule 65). It replaced the `highlight` fill with
// hand-made `filter: brightness` states. `primary` has no disabled step, so
// the disabled law is declared here once: a disabled primary gives up its
// ink fill for the `field-disabled` pair.
export const AdminActionBtnPrimary = {
  extends: 'AdminActionBtn',
  theme: 'primary',
  background: null,
  color: null,
  fontWeight: '700',
  ':hover': { background: null },
  ':active': { background: null },
  ':disabled': { theme: 'field-disabled' }
}

// A section card on the admin sheet. The sheet is flat (Nika 2026-09-24, Q2
// (B); 6f7c15865): AppMain paints the frame pair, gray2 dark / gray16 light,
// and the branded `card` theme (gray3 / gray15 — CommonCard is that card now)
// is the one fill a step above it: 1.19:1 dark, 1.08:1 light (brand-language
// skill, "cards on sheets = card"), at the CARD radius — `radiusControl` is
// the control radius. The `overlay` wash and `common-box` read about 1.00:1 on
// the sheet, so every admin card that used them was invisible.
export const AdminCard = {
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'radiusCard',
  theme: 'card',
  // Admin content sits on an opaque AppMain sheet, not the ambient canvas.
  background: 'cardSolid',
  backdropFilter: 'none',
  minWidth: '0'
}

// The card's heading: an h2 under the page's h1 (a card nested under a
// section sets its own `tag`). A1 at 700 — one step under the page title
// (PageHeaderTitle, B at 700), never above it.
export const AdminCardTitle = {
  tag: 'h2',
  margin: '0',
  fontSize: 'A2',
  fontWeight: '700',
  color: 'title'
}

// The card's description line.
export const AdminCardDesc = {
  tag: 'p',
  margin: '0',
  fontSize: 'Z2',
  color: 'caption'
}
